import { useEffect, useRef, useState } from 'react';
import { Alert, Container, LinearProgress } from '@mui/material';
import { app as teamsApp, authentication, pages } from '@microsoft/teams-js';
import { TeamsAuthRuntime, TeamsContext } from '../store/teamsSetupStore';
import { SelectedBoardResult } from './components/SetBoardDialog';
import TeamsAuthentication from './components/TeamsAuthentication';

type TeamsRuntime = TeamsAuthRuntime & {
  app: {
    initialize: () => Promise<void>;
    getContext: () => Promise<TeamsContext>;
    notifySuccess?: () => void;
  };
  pages: {
    config: {
      setValidityState: (isValid: boolean) => void;
      registerOnSaveHandler: (
        handler: (saveEvent: {
          notifySuccess: () => void;
          notifyFailure: (reason?: string) => void;
        }) => void
      ) => void;
      setConfig: (config: {
        entityId: string;
        contentUrl: string;
        websiteUrl: string;
        suggestedDisplayName: string;
      }) => Promise<void>;
    };
  };
};

type TeamsTabConfig = {
  entityId: string;
  contentUrl: string;
  websiteUrl: string;
  suggestedDisplayName: string;
};

const buildTeamsRuntime = (): TeamsRuntime => ({
  app: {
    initialize: () => teamsApp.initialize(),
    getContext: () => teamsApp.getContext() as unknown as Promise<TeamsContext>,
    notifySuccess: teamsApp.notifySuccess,
  },
  pages: {
    config: {
      setValidityState: (isValid) => pages.config.setValidityState(isValid),
      registerOnSaveHandler: (handler) => pages.config.registerOnSaveHandler(handler),
      setConfig: (config) => pages.config.setConfig(config),
    },
  },
  authentication: {
    getAuthToken: (request) =>
      authentication.getAuthToken(
        request as Parameters<typeof authentication.getAuthToken>[0]
      ),
    authenticate: (request) =>
      authentication.authenticate(request as Parameters<typeof authentication.authenticate>[0]),
  },
});

const TeamsSetupPage = () => {
  const [context, setContext] = useState<TeamsContext | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isSetBoardSectionVisible, setIsSetBoardSectionVisible] = useState(false);
  const [boardReadyMessage, setBoardReadyMessage] = useState<string | null>(null);

  const teamsRef = useRef<TeamsRuntime | null>(null);
  const pendingTabConfigRef = useRef<TeamsTabConfig | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const teams = buildTeamsRuntime();
        teamsRef.current = teams;
        await teams.app.initialize();

        const ctx = await teams.app.getContext();
        setContext(ctx);

        teams.pages.config.registerOnSaveHandler(async (saveEvent) => {
          const nextConfig = pendingTabConfigRef.current;
          if (!nextConfig) {
            saveEvent.notifyFailure(
              'Select an existing board or create a new board before saving this Teams tab.'
            );
            return;
          }

          try {
            await teams.pages.config.setConfig(nextConfig);
            saveEvent.notifySuccess();
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Failed to save Teams tab configuration.';
            saveEvent.notifyFailure(message);
          }
        });

        teams.pages.config.setValidityState(false);
        setIsReady(true);
        teams.app.notifySuccess?.();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to initialize Teams setup.';
        setInitError(message);
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBoardSelected = (result: SelectedBoardResult) => {
    pendingTabConfigRef.current = {
      entityId: result.boardId,
      suggestedDisplayName: result.title,
      contentUrl: result.boardUrl,
      websiteUrl: result.boardUrl,
    };

    teamsRef.current?.pages.config.setValidityState(true);
    setIsSetBoardSectionVisible(result.selectionType !== 'created');
    setBoardReadyMessage(
      result.selectionType === 'created'
        ? 'Your dotstorming board is ready to use, click "Save" to add it to your channel'
        : null
    );
  };

  return (
    <Container>
      <TeamsAuthentication
        teams={teamsRef.current}
        context={context}
        isReady={isReady}
        initError={initError}
        isSetBoardSectionVisible={isSetBoardSectionVisible}
        onAuthenticated={() => setIsSetBoardSectionVisible(true)}
        onBoardSelected={handleBoardSelected}
      />

      {initError ? <Alert severity="error">{initError}</Alert> : null}
      {!initError && !isReady ? <LinearProgress sx={{ mt: 2 }} /> : null}
      {!initError && isReady && boardReadyMessage ? (
        <Alert severity="success" sx={{ m: 2 }}>
          {boardReadyMessage}
        </Alert>
      ) : null}
    </Container>
  );
};

export default TeamsSetupPage;
